import { useState, useRef, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Drawer } from '@/components/shared/Drawer'
import Input from '@/components/shared/molecules/CustomInput'
import { Button } from '@/components/shared/atoms/Button'
import { useScrapeProduct } from '@/hooks/api/useProducts'
import type { ScrapedProductDto } from '@wtf/shared'
import { usePostHog } from 'posthog-js/react'
import { AnalyticsEvents } from '@/utils/analytics'

const MAX_WAIT = 7
// Microcopy messages to entertain users during wait
const messages = [
    'Unfolding the runway…',
    'Chatting with the catwalk…',
    'Stealing style secrets…',
    'Polishing pixels…',
    'Dressing up the form…',
    'Modeling…',
    'Hang tight!',
]

// Create Zod schema for form validation
const productSchema = z.object({
    purchaseUrl: z
        .string()
        .min(1, 'Product URL is required')
        .url('Please enter a valid URL'),
})

// Type inference from the Zod schema
type ProductFormValues = z.infer<typeof productSchema>

/**
 * Bottom sheet component for creating a new product
 *
 * @param open - Whether the bottom sheet is open
 * @param onClose - Function to call when the bottom sheet is closed
 * @param onEditProductOpen - Function to call to open the edit product bottom sheet
 * @param onNewProductClose - Optional function to call when the new product flow is closed
 * @param onScraped - Callback to handle scraped product data
 * @param initialUrl - Optional pre-filled URL (from Share Extension) to auto-submit
 */
export const NewProductURLBottomSheet = ({
    open,
    onClose,
    onEditProductOpen,
    onNewProductClose,
    onScraped,
    initialUrl,
}: {
    open: boolean
    onClose: () => void
    onEditProductOpen: () => void
    onNewProductClose?: () => void
    onScraped: (data: ScrapedProductDto) => void
    /** Pre-filled URL to auto-scrape (from Share Extension) */
    initialUrl?: string
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const [countdown, setCountdown] = useState<number>(MAX_WAIT)
    const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false)

    const [microcopy, setMicrocopy] = useState<string>(messages[0])
    const countdownRef = useRef<number | null>(null)
    const microcopyRef = useRef<number | null>(null)
    const { mutateAsync: scrapeProduct } = useScrapeProduct()
    const posthog = usePostHog()

    // Set up form handling with react-hook-form and zod validation
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm<ProductFormValues>({
        resolver: zodResolver(productSchema),
        defaultValues: {
            purchaseUrl: initialUrl || '',
        },
    })

    /**
     * Handle form submission to create a new product
     *
     * @param data - Form data containing the product URL
     */
    const onSubmit = useCallback(
        async (data: ProductFormValues) => {
            setIsLoading(true)

            posthog?.capture(AnalyticsEvents.PRODUCT_IMPORT_STARTED, {
                source: initialUrl ? 'share_extension' : 'manual',
                has_url: !!data.purchaseUrl,
            })

            // reset countdown and microcopy
            setCountdown(MAX_WAIT)
            setMicrocopy(messages[0])
            // start countdown every second
            countdownRef.current = window.setInterval(() => {
                setCountdown((c) => Math.max(c - 1, 0))
            }, 1000)

            // start rotating microcopy every 2 seconds
            microcopyRef.current = window.setInterval(() => {
                setMicrocopy(messages[Math.floor(Math.random() * messages.length)])
            }, 2000)
            try {
                const scrapedData = await scrapeProduct(data.purchaseUrl)
                console.log('[NewProductURLBottomSheet] scrapedData:', scrapedData)
                onScraped(scrapedData)
                // move to edit flow
                reset()
                setIsLoading(false)
                // clear countdown timer
                if (countdownRef.current) {
                    clearInterval(countdownRef.current)
                    countdownRef.current = null
                }
                // clear microcopy timer
                if (microcopyRef.current) {
                    clearInterval(microcopyRef.current)
                    microcopyRef.current = null
                }
                onEditProductOpen()
                onClose()
            } catch (error) {
                console.error('Error scraping product:', error)
                setIsLoading(false)
                if (countdownRef.current) {
                    clearInterval(countdownRef.current)
                    countdownRef.current = null
                }
                if (microcopyRef.current) {
                    clearInterval(microcopyRef.current)
                    microcopyRef.current = null
                }
            }
        },
        [
            setIsLoading,
            posthog,
            initialUrl,
            setCountdown,
            setMicrocopy,
            scrapeProduct,
            onScraped,
            reset,
            onEditProductOpen,
            onClose,
        ]
    )

    // Auto-submit when initialUrl is provided
    useEffect(() => {
        if (initialUrl && open && !hasAutoSubmitted && !isLoading) {
            setHasAutoSubmitted(true)
            setValue('purchaseUrl', initialUrl)
            // Trigger form submission after a brief delay to ensure form is populated
            const timer = setTimeout(() => {
                handleSubmit(onSubmit)()
            }, 100)
            return () => clearTimeout(timer)
        }
    }, [initialUrl, open, hasAutoSubmitted, isLoading, setValue, handleSubmit, onSubmit])

    /**
     * Handle closing the bottom sheet
     * Resets the form and calls necessary close handlers
     */
    const handleClose = () => {
        // clear any running countdown and microcopy timers
        if (countdownRef.current) {
            clearInterval(countdownRef.current)
            countdownRef.current = null
        }
        if (microcopyRef.current) {
            clearInterval(microcopyRef.current)
            microcopyRef.current = null
        }
        reset()
        setHasAutoSubmitted(false) // Reset auto-submit flag
        onClose()
        onNewProductClose?.()
    }

    return (
        <Drawer
            open={open}
            onOpenChange={handleClose}
            triggerContent={null}
            contentClassName="md:w-1/3 md:min-w-md"
            position="bottom"
            drawerHeader={{
                title: 'New product',
                onClick: handleClose,
            }}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                {/* URL input field with validation */}
                <Input
                    placeholder="Enter Product URL"
                    {...register('purchaseUrl')}
                    error={errors.purchaseUrl?.message}
                    autoComplete="off"
                />

                {isLoading && (
                    <p className="mt-2 text-sm text-gray-500">
                        {microcopy} {countdown}s remaining
                    </p>
                )}

                {/* Submit button */}
                <div className="mt-4 mb-8 flex flex-col gap-2 md:mb-0">
                    <Button type="submit" fullWidth disabled={isLoading}>
                        {isLoading ? `Adding... (${countdown}s)` : 'Add'}
                    </Button>
                </div>
            </form>
        </Drawer>
    )
}
